import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useToast,
  Badge,
  HStack,
  VStack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { FiEdit, FiTrash2, FiEye, FiPlus, FiCheck } from 'react-icons/fi';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './PoliticaPrivacidadeAdmin.css';

// API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const PoliticaPrivacidadeAdmin = () => {
  const [politicasList, setPoliticasList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: 'Política de Privacidade',
    content: '',
    version: '',
    isActive: false
  });
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [viewingPolitica, setViewingPolitica] = useState(null);
  
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  useEffect(() => {
    fetchPoliticas();
  }, []);

  const fetchPoliticas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/politica-privacidade`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setPoliticasList(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar políticas:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar políticas de privacidade',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (politica = null) => {
    if (politica) {
      setFormData({
        title: politica.title,
        content: politica.content,
        version: politica.version,
        isActive: politica.isActive
      });
      setEditingId(politica._id);
    } else {
      // For new policy, suggest next version
      const latestVersion = politicasList.length > 0 
        ? Math.max(...politicasList.map(p => parseFloat(p.version) || 0))
        : 0;
      setFormData({
        title: 'Política de Privacidade',
        content: '',
        version: (latestVersion + 0.1).toFixed(1),
        isActive: false
      });
      setEditingId(null);
    }
    onOpen();
  };

  const handleCloseModal = () => {
    setFormData({
      title: 'Política de Privacidade',
      content: '',
      version: '',
      isActive: false
    });
    setEditingId(null);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      if (!formData.content || !formData.version) {
        toast({
          title: 'Erro',
          description: 'Por favor, preencha todos os campos obrigatórios',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const token = localStorage.getItem('token');
      const endpoint = editingId 
        ? `${API_BASE_URL}/api/politica-privacidade/${editingId}`
        : `${API_BASE_URL}/api/politica-privacidade`;
      
      const method = editingId ? 'put' : 'post';
      
      const response = await axios[method](endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        toast({
          title: 'Sucesso',
          description: editingId 
            ? 'Política de privacidade atualizada com sucesso'
            : 'Política de privacidade criada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchPoliticas();
        handleCloseModal();
      }
    } catch (error) {
      console.error('Erro ao salvar política:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao salvar política de privacidade',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleActivate = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_BASE_URL}/api/politica-privacidade/${id}/activate`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.success) {
        toast({
          title: 'Sucesso',
          description: 'Política de privacidade ativada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchPoliticas();
      }
    } catch (error) {
      console.error('Erro ao ativar política:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao ativar política de privacidade',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_BASE_URL}/api/politica-privacidade/${deletingId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.success) {
        toast({
          title: 'Sucesso',
          description: 'Política de privacidade excluída com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchPoliticas();
        onDeleteClose();
        setDeletingId(null);
      }
    } catch (error) {
      console.error('Erro ao excluir política:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao excluir política de privacidade',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleView = (politica) => {
    setViewingPolitica(politica);
    onViewOpen();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxW="container.xl" py={5}>
      <VStack spacing={5} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg">Gerenciar Política de Privacidade</Heading>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="green"
            onClick={() => handleOpenModal()}
          >
            Nova Política
          </Button>
        </HStack>

        {loading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="xl" color="green.500" />
          </Box>
        ) : politicasList.length === 0 ? (
          <Alert status="info">
            <AlertIcon />
            Nenhuma política de privacidade cadastrada. Clique em "Nova Política" para criar a primeira.
          </Alert>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Título</Th>
                  <Th>Versão</Th>
                  <Th>Status</Th>
                  <Th>Criado em</Th>
                  <Th>Atualizado em</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {politicasList.map((politica) => (
                  <Tr key={politica._id}>
                    <Td>{politica.title}</Td>
                    <Td>{politica.version}</Td>
                    <Td>
                      <Badge colorScheme={politica.isActive ? 'green' : 'gray'}>
                        {politica.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </Td>
                    <Td>{formatDate(politica.createdAt)}</Td>
                    <Td>{formatDate(politica.updatedAt)}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Visualizar"
                          icon={<FiEye />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleView(politica)}
                        />
                        <IconButton
                          aria-label="Editar"
                          icon={<FiEdit />}
                          size="sm"
                          colorScheme="yellow"
                          variant="ghost"
                          onClick={() => handleOpenModal(politica)}
                        />
                        {!politica.isActive && (
                          <>
                            <IconButton
                              aria-label="Ativar"
                              icon={<FiCheck />}
                              size="sm"
                              colorScheme="green"
                              variant="ghost"
                              onClick={() => handleActivate(politica._id)}
                            />
                            <IconButton
                              aria-label="Excluir"
                              icon={<FiTrash2 />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => {
                                setDeletingId(politica._id);
                                onDeleteOpen();
                              }}
                            />
                          </>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>
            {editingId ? 'Editar Política de Privacidade' : 'Criar Nova Política de Privacidade'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Título</FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Política de Privacidade"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Versão</FormLabel>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="Ex: 1.0"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Conteúdo</FormLabel>
                <Box className="quill-container">
                  <ReactQuill
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    modules={modules}
                    theme="snow"
                    placeholder="Digite o conteúdo da política de privacidade..."
                    style={{ height: '300px', marginBottom: '50px' }}
                  />
                </Box>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">
                  Ativar esta política?
                </FormLabel>
                <Switch
                  isChecked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  colorScheme="green"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button colorScheme="green" onClick={handleSubmit}>
              {editingId ? 'Atualizar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>
            {viewingPolitica?.title} - Versão {viewingPolitica?.version}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <HStack>
                <Text fontWeight="bold">Status:</Text>
                <Badge colorScheme={viewingPolitica?.isActive ? 'green' : 'gray'}>
                  {viewingPolitica?.isActive ? 'Ativo' : 'Inativo'}
                </Badge>
              </HStack>
              <Box>
                <Text fontWeight="bold" mb={2}>Conteúdo:</Text>
                <Box
                  p={4}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  dangerouslySetInnerHTML={{ __html: viewingPolitica?.content }}
                  className="policy-preview"
                />
              </Box>
              <HStack spacing={4} fontSize="sm" color="gray.600">
                <Text>Criado em: {viewingPolitica && formatDate(viewingPolitica.createdAt)}</Text>
                <Text>Atualizado em: {viewingPolitica && formatDate(viewingPolitica.updatedAt)}</Text>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir Política de Privacidade
            </AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja excluir esta política de privacidade? Esta ação não pode ser desfeita.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default PoliticaPrivacidadeAdmin;